import { Controller } from "@hotwired/stimulus";
import { ActionEvent } from "@hotwired/stimulus";

interface ClearInputActionEvent extends ActionEvent {
  params: {
    inputName: string | undefined;
  };
}

export default class extends Controller<HTMLFormElement> {
  submitting: boolean = false;

  static targets = ["formula"];

  declare readonly formulaTargets: HTMLInputElement[];

  private changedSubmitter: (HTMLElement & { disabled: boolean }) | undefined;

  private readonly onSubmit = (event: Event): void => {
    if (this.submitting) {
      // Prevent double submission.
      event.preventDefault();
      return;
    }
    this.submitting = true;
  };

  private readonly onTurboSubmitEnd = (event: Event): void => {
    const success =
      (event as CustomEvent<{ success?: boolean }>).detail?.success === true;

    this.submitting = false;
    if (!success && this.changedSubmitter?.isConnected) {
      this.changedSubmitter.disabled = false;
    }
    this.changedSubmitter = undefined;
  };

  connect() {
    this.evaluateEmptyDataAttribute(this.element);
    this.element.addEventListener("submit", this.onSubmit);
    this.element.addEventListener("turbo:submit-end", this.onTurboSubmitEnd);
  }

  disconnect() {
    this.element.removeEventListener("submit", this.onSubmit);
    this.element.removeEventListener("turbo:submit-end", this.onTurboSubmitEnd);
  }

  submitterChanged(event: Event) {
    const form = this.element;
    const target = event.currentTarget as HTMLElement;

    // Skip submitting if the value has not changed.
    if ("value" in target && "initial" in target.dataset) {
      const initialValue = target.dataset.initial as string;
      if (target.value === initialValue) {
        return;
      }
    }

    if (!form.reportValidity()) {
      return;
    }

    form.requestSubmit();
    if ("disabled" in target) {
      this.changedSubmitter = target as HTMLElement & { disabled: boolean };
      this.changedSubmitter.disabled = true;
    }
  }

  formReset(event: Event) {
    // Add a tiny delay to make sure the form has been reset.
    setTimeout(() => {
      this.evaluateEmptyDataAttribute(event.target as HTMLFormElement);
    });
  }

  /**
   * Evaluate all date elements and set the data-empty attribute
   * when the element is empty or has no value.
   */
  private evaluateEmptyDataAttribute(form: HTMLFormElement) {
    Array.from(form.querySelectorAll("input[type='date']")).forEach(
      (element) => {
        const inputElement = element as HTMLInputElement;
        this.toggleEmptyDataAttribute(inputElement);
      }
    );
  }

  dateValueChanged(event: Event) {
    const element = event.target as HTMLInputElement;
    this.toggleEmptyDataAttribute(element);
  }

  private toggleEmptyDataAttribute(element: HTMLInputElement) {
    if (element.value.trim() !== "") {
      element.dataset.empty = "false";
    } else {
      element.dataset.empty = "true";
    }
  }

  /**
   * Clears the value of a given form element based on its type.
   * Dispatches appropriate events after clearing.
   * @param element The form element to clear.
   */
  private clearElementValue(
    element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  ) {
    if (element instanceof HTMLInputElement) {
      this.clearInputElement(element);
      return;
    }
    if (element instanceof HTMLSelectElement) {
      this.clearSelectElement(element);
      return;
    }
    if (element instanceof HTMLTextAreaElement) {
      this.clearTextAreaElement(element);
      return;
    }
  }

  private clearInputElement(element: HTMLInputElement) {
    switch (element.type) {
      case "text":
      case "email":
      case "number":
      case "date":
      case "time":
      case "password":
      case "search":
      case "tel":
      case "url":
        element.value = "";
        // Dispatch 'input' event first for immediate feedback/validation
        element.dispatchEvent(
          new InputEvent("input", { bubbles: true, cancelable: true })
        );
        // This second value assignment prevents the date fields (and maybe others?)
        // from being considered invalid after triggering the input event.
        // Why that happens? I do not know, but this solved it.
        element.value = element.value;
        break;
      case "checkbox":
      case "radio":
        if (element.checked) {
          element.checked = false;
          element.dispatchEvent(
            new Event("input", { bubbles: true, cancelable: true })
          );
        }
        break;
    }
  }

  private clearSelectElement(element: HTMLSelectElement) {
    let changed = false;
    if (element.multiple) {
      // Additional handling for 'select-multiple'
      Array.from(element.options).forEach((option) => {
        if (option.selected) {
          option.selected = false;
          changed = true;
        }
      });
    }

    if (element.required && element.selectedIndex !== 0) {
      element.selectedIndex = 0;
      changed = true;
    }

    if (changed) {
      element.dispatchEvent(
        new Event("change", { bubbles: true, cancelable: true })
      );
    }
  }

  private clearTextAreaElement(element: HTMLTextAreaElement) {
    if (element.value !== "") {
      element.value = "";
      // Dispatch 'input' event first for immediate feedback/validation
      element.dispatchEvent(
        new InputEvent("input", { bubbles: true, cancelable: true })
      );
    }
  }

  /**
   * Clears all clearable elements within the form and submits the form.
   * @param event The event that triggered the clear action.
   */
  clearForm(event: Event) {
    event.preventDefault(); // Prevent any default action if triggered by an event
    const form = this.element;

    // Iterate over all form elements and clear their values
    Array.from(form.elements).forEach((element) => {
      if (
        element &&
        (element instanceof HTMLInputElement ||
          element instanceof HTMLSelectElement ||
          element instanceof HTMLTextAreaElement)
      ) {
        this.clearElementValue(element);
      }
    });

    // Optionally submit the form after clearing
    // Consider if this is always the desired behavior
    form.requestSubmit();
  }

  /**
   * Clears the value of an input element by name within the form.
   * @param event The click event from the button/element used to trigger the clear.
   */
  clearInput(event: ClearInputActionEvent) {
    event.preventDefault();
    const clickedElement = event.currentTarget as HTMLElement;
    const inputName = event.params.inputName;

    if (!inputName) {
      console.warn("No input-name parameter provided.", clickedElement);
      return;
    }

    const form = this.element;
    const inputElement = form.querySelector(`[name="${inputName}"]`);

    if (
      inputElement &&
      (inputElement instanceof HTMLInputElement ||
        inputElement instanceof HTMLSelectElement ||
        inputElement instanceof HTMLTextAreaElement)
    ) {
      this.clearElementValue(inputElement);
      // Dispatch 'change' event for listeners reacting to final value change
      inputElement.dispatchEvent(
        new Event("change", { bubbles: true, cancelable: true })
      );
    } else {
      console.warn(
        `No input/select/textarea with name "${inputName}" found to clear.`,
        clickedElement
      );
    }
  }
}
